import { useContext, useState } from "react";

import { Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { useHead } from "@unhead/react";
import { Link, useParams } from "react-router";

import { useCurrentSession } from "@/components/helpers/custom-hooks/use-current-session";
import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { isInWorkspace } from "@/components/helpers/workspace/permissions";
import { SwitchToWorkspace } from "@/components/helpers/workspace/SwitchToWorkspace";
import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { useAuth } from "@/components/lib/auth";
import { Nav } from "@/components/nav/Nav";
import { ExploreDetailSkeleton } from "@/components/pages_flexible/explore/ExploreDetailSkeleton";
import { PageContainer } from "@/components/pages_flexible/PageContainer";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { NotFound } from "@/components/pages_public/404/NotFound";
import { ProductPrice } from "@/components/pieces/products/ProductPrice";
import { ResponseContext } from "@/components/ResponseContext";
import {
  CARD_CLASSES,
  PUBLIC_CONTENT_MAX_WIDTH,
  SIDE_NAV_WIDTH,
  YELLOW,
} from "@/css/constants";
import { ProductCategories, useServiceDetailQuery } from "@/graphql";
import { SessionCurrentUser } from "@/types/users";

import { ProductDetails } from "./ProductDetails";
import { RFQModal } from "./RFQModal";
import { BigButton, Heading } from "./styles";

const FLOATING_FULL_WIDTH = [0, 300, 450, 475];
const FLOATING_WIDTH = [0, 250, 375];
const FLOATING_PADDING = [0, 50, 75, 100];

function ServiceDetail({ currentUser }: { currentUser?: SessionCurrentUser }) {
  const { res } = useContext(ResponseContext);
  const [rfqModalVisible, setRfqModalVisible] = useState(false);

  let { productSlug, coachSlug } = useParams();
  coachSlug = coachSlug?.toLowerCase();
  productSlug = productSlug?.toLowerCase();

  const { data, loading } = useServiceDetailQuery({
    variables: {
      productSlug,
      coachSlug,
    },
    skip: !productSlug && !coachSlug,
  });

  useHead({ bodyAttrs: { class: "public-site" } });

  if (loading) {
    return <ExploreDetailSkeleton />;
  }

  const coach = data?.coachBySlug;
  const coachProduct = data?.coachBySlug?.coachProduct;
  const product = coachProduct?.product;

  if (!product) {
    if (res) {
      res.status(404);
    }
    return <NotFound />;
  }

  const seoTitle = `${product.name} | ${product.specialty?.name} | Book Online | Exec`;
  const seoDescription = product.shortDescription;

  // Just support request booking when have selected coach
  const isGroupMeeting = product.category === ProductCategories.GroupMeeting;

  const loggedInLink = coach
    ? `/schedule/${coach.id}?productId=${product.id}`
    : `/schedule?productId=${product.id}`;

  const link = currentUser ? loggedInLink : `/start?nextPath=${loggedInLink}`;

  let actionBox = (
    <div className="mt-8 bg-white p-4 rounded-lg shadow-md">
      <div className="text-xl font-bold mb-4 font-serif">Schedule Session</div>
      {product.enableRfq ? (
        <BigButton
          intent="primary"
          large={true}
          fill={true}
          onClick={() => setRfqModalVisible(true)}
        >
          Request a Quote
        </BigButton>
      ) : (
        <>
          <div className="my-8">
            {!coach ? (
              <>
                <div className="font-bold">Coach</div>
                <div className="mb-4">Selected during booking</div>
                <div className="font-bold mb-2">Price</div>
                <ProductPrice
                  creditPrice={coachProduct.coachPrice}
                  minCreditPrice={product.priceRange?.minCreditPrice}
                  maxCreditPrice={product.priceRange?.maxCreditPrice}
                />
              </>
            ) : null}
          </div>
          <Link to={link}>
            <BigButton intent="primary" large={true} fill={true}>
              Book Now
            </BigButton>
          </Link>
        </>
      )}
    </div>
  );

  const firstWorkspace = currentUser?.workspaces?.[0];
  if (currentUser && isGroupMeeting && !isInWorkspace() && firstWorkspace) {
    actionBox = (
      <Box
        className={CARD_CLASSES}
        backgroundColor="#FFF"
        p={[3, 3, 3, 20]}
        m={[3, 0]}
      >
        <SwitchToWorkspace
          workspace={firstWorkspace}
          path={`?path=/explore/${productSlug}`}
        />
      </Box>
    );
  }

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonicalDomain={
          !product
            ? undefined
            : product.primarySite === "EXEC"
              ? "https://www.exec.com"
              : "https://www.placement.com"
        }
        canonicalPath={`/explore/${productSlug}`}
        image={product.metaImageUrl}
      />
      <Box id="sticky-container" position="relative">
        <Flex
          flexDirection="column"
          alignItems="center"
          style={{
            background:
              "linear-gradient(99.62deg, #003881 33.88%, #2970C9 98.39%)",
          }}
          px={[3, 4]}
          pt={100}
          pb={5}
        >
          <Flex
            width={1}
            maxWidth={PUBLIC_CONTENT_MAX_WIDTH}
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box pr={FLOATING_PADDING}>
              <SpecialtyTag mb={2}>{product.specialty?.name}</SpecialtyTag>
              <Heading>{product.name}</Heading>
              {product.numRatings ? (
                <Flex mt={3} alignItems="center" lineHeight="14px">
                  <Icon icon="star" size={16} color={YELLOW} />
                  <Box ml={1} fontSize={14} fontWeight={600} color="#FFF">
                    {((product.averageRating || 0) + Number.EPSILON).toFixed(1)}{" "}
                    Average Rating
                  </Box>
                </Flex>
              ) : null}
              <Box color="#FFF" fontSize={24} mt={30}>
                {product.shortDescription}
              </Box>
            </Box>

            <Media greaterThan="xs">
              <Box width={FLOATING_WIDTH} height={0}>
                <Box
                  css={{
                    position: "sticky",
                    top: "70px",
                    zIndex: 1,
                  }}
                >
                  {actionBox}
                </Box>
              </Box>
            </Media>
          </Flex>
        </Flex>
        <Media at="xs">{actionBox}</Media>
        <Flex
          flexDirection="column"
          alignItems="center"
          px={[3, 4]}
          mb={120}
          pt={[4, 5]}
        >
          <Box
            width={1}
            maxWidth={PUBLIC_CONTENT_MAX_WIDTH}
            pr={FLOATING_FULL_WIDTH}
          >
            <ProductDetails product={product} coach={coach} />
          </Box>
        </Flex>
      </Box>
      <RFQModal
        product={product}
        coachId={coach?.id}
        visible={rfqModalVisible}
        onClose={() => setRfqModalVisible(false)}
      />
    </>
  );
}

function AuthServiceDetail() {
  const { currentUser } = useAuth();
  return (
    <Authenticate maxWidth="100%" layout="full">
      <ServiceDetail currentUser={currentUser} />
    </Authenticate>
  );
}

export function ExplorePage() {
  const { currentSessionLink } = useCurrentSession();

  if (!import.meta.env.SSR && currentSessionLink) {
    return (
      <Authenticate maxWidth="100%" layout="full">
        <>
          <Nav />
          <Box ml={[0, 0, SIDE_NAV_WIDTH]}>
            <AuthServiceDetail />
          </Box>
        </>
      </Authenticate>
    );
  }

  return (
    <PageContainer>
      <ServiceDetail />
    </PageContainer>
  );
}

const SpecialtyTag = styled(Box)`
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.05);
  background-color: rgba(255, 255, 255, 0.1);
  letter-spacing: 0.5px;
  font-size: 13px;
  padding: 5px 12px;
  font-weight: 500;
  border-radius: 100px;
  display: inline-block;
`;
